import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Heading,
  Box,
  Text,
  SimpleGrid,
  Image,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatGroup,
  Tag,
  Badge,
  Tooltip,
  Flex,
  Select,
  Center,
  Spinner,
  Wrap,
  WrapItem,
  Button,
  Avatar,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Link,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { TriangleUpIcon, TriangleDownIcon, StarIcon } from '@chakra-ui/icons';
import { supabase } from '../supabaseClient';
import { brandColors, chartColors, positionConfig } from '../config/styleConfig';

// Register Chart.js components needed for this dashboard.
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend
);

// Local interfaces mirror the shape of existing Supabase data used elsewhere in the app.
interface RaceMeta {
  race_code: string;
  race_description: string;
  race_date: string;
  race_location: string;
  race_discipline?: string | null;
  race_season?: string | null;
  race_league?: string | null;
}

interface RaceResult {
  id: number;
  name: string;
  team: string | null;
  position: number;
  race_category: string;
  race_code: string;
}

interface RacerSummary {
  name: string;
  team: string | null;
  best_position: number;
  race_category: string;
  year: number | null;
  best_time: string | null;
  last_race: string | null;
  num_races: number;
}

// Golden stats are optional; when available they can be used to refine racer evolution metrics.
interface GoldenRaceResult {
  name: string;
  team: string | null;
  position: number;
  race_code: string;
}

interface TeamSeasonStats {
  team: string;
  memberCount: number;
  totalStarts: number;
  points: number;
  podiumFinishes: number;
  winRate: number;
}

interface BiggestCompetitor {
  name: string;
  team: string | null;
  seasonStarts: number;
  totalStarts: number;
  bestPosition: number;
  lastRace: string | null;
}

interface JumperOfYear {
  name: string;
  team: string | null;
  seasonStarts: number;
  firstHalfAvg: number;
  secondHalfAvg: number;
  improvement: number;
}

interface ConsistencyBeast {
  name: string;
  team: string | null;
  seasonStarts: number;
  averagePosition: number;
  stddevPosition: number;
}

const POINTS_PER_POSITION: Record<number, number> = {
  1: 100,
  2: 80,
  3: 60,
  4: 50,
  5: 45,
  6: 40,
};

const getPointsForPosition = (position: number | null | undefined): number =>
  position && position > 0 ? POINTS_PER_POSITION[position] ?? 0 : 0;

const formatShortDate = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
};

const formatMonthLabel = (value: string): string => {
  // Expecting YYYY-MM or full ISO date string; fallback to input when parsing fails.
  if (value.length >= 7) {
    const [year, month] = value.split('-');
    if (year && month) {
      return `${month}/${year.slice(-2)}`;
    }
  }
  return value;
};

const formatPosition = (position: number): string =>
  position === 0 ? 'DNF' : `${position}.`;

const tabSelectedStyles = {
  bg: 'blue.500',
  color: 'white',
  borderColor: 'blue.500',
  borderBottomColor: 'white',
};

export default function Statistics() {
  const navigate = useNavigate();
  const [raceMeta, setRaceMeta] = useState<RaceMeta[]>([]);
  const [raceResults, setRaceResults] = useState<RaceResult[]>([]);
  const [racersSummary, setRacersSummary] = useState<RacerSummary[]>([]);
  const [goldenResults, setGoldenResults] = useState<GoldenRaceResult[]>([]);
  const [seasons, setSeasons] = useState<string[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<string>('all');
  const [leagues, setLeagues] = useState<string[]>([]);
  const [selectedLeague, setSelectedLeague] = useState<string>('all');
  const [selectedTeamForChart, setSelectedTeamForChart] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Racers by country: fetched from DB function get_frontend_country_stats() (gold_country, count distinct gold_name)
  const [countryStats, setCountryStats] = useState<{ country: string; num_racers: number }[]>([]);

  // Team leaderboard sort: key + direction, or null for off (default order)
  type TeamLeaderboardSortKey = keyof TeamSeasonStats;
  const [teamLeaderboardSort, setTeamLeaderboardSort] = useState<{
    key: TeamLeaderboardSortKey;
    direction: 'asc' | 'desc';
  } | null>(null);

  const cardBg = useColorModeValue('white', 'gray.800');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setFetchError(null);

        // Load race metadata – seasons and leagues are derived from this table.
        const { data: raceMetaData, error: raceMetaError } = await supabase
          .from('medvedica_race_data')
          .select('*')
          .order('race_date', { ascending: true });

        if (raceMetaError) {
          console.error('Error fetching race metadata for statistics:', raceMetaError);
          setFetchError('Unable to load race metadata. Statistics page may be incomplete.');
        } else {
          const typedRaceMeta = (raceMetaData ?? []) as RaceMeta[];
          setRaceMeta(typedRaceMeta);

          const derivedSeasons = Array.from(
            new Set(
              typedRaceMeta
                .map((race) => race.race_season)
                .filter((season): season is string => Boolean(season))
            )
          ).sort((a, b) => b.localeCompare(a));
          setSeasons(derivedSeasons);
        }

        // Base per-result stats – used for most of the team and timeline aggregations.
        const { data: raceStatsData, error: raceStatsError } = await supabase
          .from('medvedica_race_stats')
          .select('id, name, team, position, race_category, race_code');

        if (raceStatsError) {
          console.error('Error fetching race stats for statistics:', raceStatsError);
        } else if (raceStatsData) {
          setRaceResults(raceStatsData as RaceResult[]);
        }

        // Golden racer summary – same source as /racers page.
        const { data: racersData, error: racersError } = await supabase
          .from('frontend_racers_summary_mv')
          .select('*');

        if (racersError) {
          // 57014 = statement timeout (same behaviour as Racers page).
          const code = (racersError as { code?: string }).code;
          console.error('Error fetching racer summary for statistics:', code, racersError);
          if (code === '57014') {
            setFetchError(
              'Racer summary request timed out. Some racer statistics may be missing; try again later.'
            );
          }
        } else if (racersData) {
          const typedData = racersData as any[];
          const mapped: RacerSummary[] = typedData.map((record) => ({
            name: record.gold_name,
            team: record.gold_team,
            race_category: record.gold_country || '',
            best_position: record.best_position,
            year: record.gold_year ? parseInt(record.gold_year as string, 10) : null,
            best_time: record.best_time,
            last_race: record.last_race,
            num_races: record.num_races,
          }));
          setRacersSummary(mapped);
        }

        // Racers by country: use DB function for correct distinct racer counts per country (gold_country from MV).
        const { data: countryStatsData, error: countryStatsError } = await (supabase as any).rpc(
          'get_frontend_country_stats'
        );
        if (countryStatsError) {
          console.warn('Error fetching country stats for Racers by country graph:', countryStatsError);
          setCountryStats([]);
        } else if (countryStatsData && Array.isArray(countryStatsData)) {
          const typed = countryStatsData as { country: string; num_racers: number }[];
          setCountryStats(typed);
        } else {
          setCountryStats([]);
        }

        // Optional golden stats table – used for longitudinal racer metrics when available.
        const { data: goldenData, error: goldenError } = await supabase
          .from('medvedica_race_stats_golden')
          .select('name, team, position, race_code');

        if (goldenError) {
          console.warn(
            'Golden stats table not available or query failed. Falling back to raw race stats only for evolution metrics.',
            goldenError
          );
          setGoldenResults([]);
        } else if (goldenData) {
          const typedGolden = goldenData as any[];
          const mappedGolden: GoldenRaceResult[] = typedGolden.map((row) => ({
            name: row.name,
            team: row.team ?? null,
            position: row.position,
            race_code: row.race_code,
          }));
          setGoldenResults(mappedGolden);
        }
      } catch (e) {
        console.error('Unexpected error while loading statistics data:', e);
        setFetchError('Unexpected error while loading statistics data.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Recompute available leagues whenever base race metadata or selected season changes.
  useEffect(() => {
    const baseRaces =
      selectedSeason === 'all'
        ? raceMeta
        : raceMeta.filter((race) => race.race_season === selectedSeason);

    const uniqueLeagues = Array.from(
      new Set(
        baseRaces
          .map((race) => race.race_league)
          .filter((league): league is string => Boolean(league))
      )
    ).sort((a, b) => a.localeCompare(b));

    setLeagues(uniqueLeagues);
    // Reset league filter when the underlying season scope changes.
    setSelectedLeague('all');
  }, [raceMeta, selectedSeason]);

  const raceCodeToMeta = useMemo(() => {
    const map: Record<string, RaceMeta> = {};
    raceMeta.forEach((race) => {
      map[race.race_code] = race;
    });
    return map;
  }, [raceMeta]);

  const filteredRaceMeta = useMemo(
    () =>
      raceMeta.filter((race) => {
        const matchesSeason =
          selectedSeason === 'all' || race.race_season === selectedSeason;
        const matchesLeague =
          selectedLeague === 'all' || race.race_league === selectedLeague;
        return matchesSeason && matchesLeague;
      }),
    [raceMeta, selectedSeason, selectedLeague]
  );

  const filteredRaceResults = useMemo(
    () =>
      raceResults.filter((result) => {
        const meta = raceCodeToMeta[result.race_code];
        if (!meta) {
          return false;
        }
        const matchesSeason =
          selectedSeason === 'all' || meta.race_season === selectedSeason;
        const matchesLeague =
          selectedLeague === 'all' || meta.race_league === selectedLeague;
        return matchesSeason && matchesLeague;
      }),
    [raceResults, raceCodeToMeta, selectedSeason, selectedLeague]
  );

  const racerSummaryByName = useMemo(() => {
    const map = new Map<string, RacerSummary>();
    racersSummary.forEach((racer) => {
      map.set(racer.name, racer);
    });
    return map;
  }, [racersSummary]);

  const racerTeamByName = useMemo(() => {
    const map = new Map<string, string | null>();
    filteredRaceResults.forEach((result) => {
      if (!map.has(result.name)) {
        map.set(result.name, result.team ?? null);
      }
    });
    return map;
  }, [filteredRaceResults]);

  const teamSeasonStats: TeamSeasonStats[] = useMemo(() => {
    if (!filteredRaceResults.length) {
      return [];
    }

    const perTeam: Record<
      string,
      { memberSet: Set<string>; totalStarts: number; points: number; podiumFinishes: number }
    > = {};

    filteredRaceResults.forEach((result) => {
      if (!result.team) {
        return;
      }
      if (!perTeam[result.team]) {
        perTeam[result.team] = {
          memberSet: new Set<string>(),
          totalStarts: 0,
          points: 0,
          podiumFinishes: 0,
        };
      }
      const entry = perTeam[result.team];
      entry.memberSet.add(result.name);
      entry.totalStarts += 1;
      const points = getPointsForPosition(result.position);
      entry.points += points;
      if (result.position >= 1 && result.position <= 3) {
        entry.podiumFinishes += 1;
      }
    });

    const stats: TeamSeasonStats[] = Object.entries(perTeam).map(
      ([teamName, value]) => {
        const memberCount = value.memberSet.size || 1;
        return {
          team: teamName,
          memberCount,
          totalStarts: value.totalStarts,
          points: value.points,
          podiumFinishes: value.podiumFinishes,
          winRate: value.points / memberCount,
        };
      }
    );

    return stats.sort((a, b) => b.points - a.points);
  }, [filteredRaceResults]);

  useEffect(() => {
    if (teamSeasonStats.length && !selectedTeamForChart) {
      setSelectedTeamForChart(teamSeasonStats[0].team);
    }
  }, [teamSeasonStats, selectedTeamForChart]);

  // Sorted team leaderboard for display (sort by column + direction, or default points desc)
  const sortedTeamLeaderboard = useMemo(() => {
    if (!teamLeaderboardSort) {
      return [...teamSeasonStats];
    }
    const { key, direction } = teamLeaderboardSort;
    return [...teamSeasonStats].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];
      const isNum = typeof aVal === 'number' && typeof bVal === 'number';
      let cmp: number;
      if (isNum) {
        cmp = (aVal as number) - (bVal as number);
      } else {
        cmp = String(aVal).localeCompare(String(bVal));
      }
      return direction === 'asc' ? cmp : -cmp;
    });
  }, [teamSeasonStats, teamLeaderboardSort]);

  const handleTeamLeaderboardSort = (key: TeamLeaderboardSortKey) => {
    setTeamLeaderboardSort((prev) => {
      if (prev?.key !== key) return { key, direction: 'asc' as const };
      if (prev.direction === 'asc') return { key, direction: 'desc' as const };
      return null; // off
    });
  };

  const totalRaces = filteredRaceMeta.length;
  const totalStarts = filteredRaceResults.length;
  const totalRacers = useMemo(
    () => new Set(filteredRaceResults.map((r) => r.name)).size,
    [filteredRaceResults]
  );
  const totalClubs = useMemo(
    () =>
      new Set(
        filteredRaceResults
          .map((r) => r.team)
          .filter((team): team is string => Boolean(team))
      ).size,
    [filteredRaceResults]
  );

  const topTeamsForOverview = teamSeasonStats.slice(0, 8);

  const disciplineDistribution = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredRaceMeta.forEach((race) => {
      const discipline = (race.race_discipline || 'Unknown') as string;
      counts[discipline] = (counts[discipline] || 0) + 1;
    });
    const labels = Object.keys(counts);
    const data = labels.map((key) => counts[key]);
    return { labels, data };
  }, [filteredRaceMeta]);

  // Teams by country: assign each team a primary country (most common among its racers), then count teams per country
  const teamsByCountry = useMemo(() => {
    const teamCountryCounts: Record<string, Record<string, number>> = {};
    racersSummary.forEach((r) => {
      if (!r.team) return;
      const country = (r.race_category || '').trim() || 'Unknown';
      if (!teamCountryCounts[r.team]) teamCountryCounts[r.team] = {};
      teamCountryCounts[r.team][country] = (teamCountryCounts[r.team][country] || 0) + 1;
    });
    const teamToPrimaryCountry: Record<string, string> = {};
    Object.entries(teamCountryCounts).forEach(([team, counts]) => {
      const entries = Object.entries(counts);
      if (entries.length === 0) return;
      const best = entries.sort((a, b) => b[1] - a[1])[0];
      teamToPrimaryCountry[team] = best[0];
    });
    const countryCount: Record<string, number> = {};
    teamSeasonStats.forEach((t) => {
      const country = teamToPrimaryCountry[t.team] || 'Unknown';
      countryCount[country] = (countryCount[country] || 0) + 1;
    });
    const labels = Object.keys(countryCount).sort(
      (a, b) => (countryCount[b] || 0) - (countryCount[a] || 0)
    );
    const data = labels.map((c) => countryCount[c]);
    return { labels, data };
  }, [racersSummary, teamSeasonStats]);

  // Racers by country: from DB function get_frontend_country_stats(); show top 8 by num_racers
  const racersByCountry = useMemo(() => {
    const sorted = [...countryStats].sort((a, b) => (b.num_racers || 0) - (a.num_racers || 0));
    const top8 = sorted.slice(0, 8);
    const labels = top8.map((r) => r.country);
    const data = top8.map((r) => Number(r.num_racers));
    return { labels, data };
  }, [countryStats]);

  const startsByRacer = useMemo(() => {
    const map = new Map<string, number>();
    filteredRaceResults.forEach((result) => {
      map.set(result.name, (map.get(result.name) ?? 0) + 1);
    });
    return map;
  }, [filteredRaceResults]);

  const biggestCompetitors: BiggestCompetitor[] = useMemo(() => {
    const competitors: BiggestCompetitor[] = [];
    startsByRacer.forEach((seasonStarts, name) => {
      const summary = racerSummaryByName.get(name);
      const totalStarts = summary?.num_races ?? seasonStarts;
      const bestPosition = summary?.best_position ?? 0;
      const lastRace = summary?.last_race ?? null;
      const team = summary?.team ?? racerTeamByName.get(name) ?? null;

      competitors.push({
        name,
        team,
        seasonStarts,
        totalStarts,
        bestPosition,
        lastRace,
      });
    });

    return competitors
      .sort((a, b) => {
        if (b.seasonStarts !== a.seasonStarts) {
          return b.seasonStarts - a.seasonStarts;
        }
        if (b.totalStarts !== a.totalStarts) {
          return b.totalStarts - a.totalStarts;
        }
        if (a.bestPosition === 0 || b.bestPosition === 0) {
          return (a.bestPosition || Number.MAX_SAFE_INTEGER) -
            (b.bestPosition || Number.MAX_SAFE_INTEGER);
        }
        return a.bestPosition - b.bestPosition;
      })
      .slice(0, 6);
  }, [startsByRacer, racerSummaryByName, racerTeamByName]);

  const positionsByRacer = useMemo(() => {
    const map = new Map<string, { date: string; position: number }[]>();
    filteredRaceResults.forEach((result) => {
      const meta = raceCodeToMeta[result.race_code];
      if (!meta) {
        return;
      }
      const list = map.get(result.name) ?? [];
      list.push({ date: meta.race_date, position: result.position });
      map.set(result.name, list);
    });

    map.forEach((list, name) => {
      list.sort((a, b) => a.date.localeCompare(b.date));
      map.set(name, list);
    });

    return map;
  }, [filteredRaceResults, raceCodeToMeta]);

  const jumpersOfYear: JumperOfYear[] = useMemo(() => {
    const jumpers: JumperOfYear[] = [];

    positionsByRacer.forEach((entries, name) => {
      const valid = entries.filter(
        (entry) => entry.position && entry.position > 0
      );
      if (valid.length < 3) {
        return;
      }
      const mid = Math.floor(valid.length / 2);
      const firstHalf = valid.slice(0, mid);
      const secondHalf = valid.slice(mid);
      if (!firstHalf.length || !secondHalf.length) {
        return;
      }
      const avg = (data: { position: number }[]): number =>
        data.reduce((sum, item) => sum + item.position, 0) / data.length;

      const firstAvg = avg(firstHalf);
      const secondAvg = avg(secondHalf);
      const improvement = firstAvg - secondAvg;
      if (improvement <= 0) {
        return;
      }

      const summary = racerSummaryByName.get(name);
      const team = summary?.team ?? racerTeamByName.get(name) ?? null;

      jumpers.push({
        name,
        team,
        seasonStarts: valid.length,
        firstHalfAvg: firstAvg,
        secondHalfAvg: secondAvg,
        improvement,
      });
    });

    return jumpers
      .sort((a, b) => b.improvement - a.improvement)
      .slice(0, 6);
  }, [positionsByRacer, racerSummaryByName, racerTeamByName]);

  const consistencyBeasts: ConsistencyBeast[] = useMemo(() => {
    const beasts: ConsistencyBeast[] = [];

    positionsByRacer.forEach((entries, name) => {
      const valid = entries.filter(
        (entry) => entry.position && entry.position > 0
      );
      if (valid.length < 4) {
        return;
      }
      const avgPosition =
        valid.reduce((sum, item) => sum + item.position, 0) / valid.length;
      const variance =
        valid.reduce(
          (sum, item) => sum + (item.position - avgPosition) ** 2,
          0
        ) / valid.length;
      const stddev = Math.sqrt(variance);

      if (avgPosition <= 10 && stddev <= 3) {
        const summary = racerSummaryByName.get(name);
        const team = summary?.team ?? racerTeamByName.get(name) ?? null;
        beasts.push({
          name,
          team,
          seasonStarts: valid.length,
          averagePosition: avgPosition,
          stddevPosition: stddev,
        });
      }
    });

    return beasts
      .sort((a, b) => {
        if (a.stddevPosition !== b.stddevPosition) {
          return a.stddevPosition - b.stddevPosition;
        }
        return a.averagePosition - b.averagePosition;
      })
      .slice(0, 6);
  }, [positionsByRacer, racerSummaryByName, racerTeamByName]);

  const teamHistoryForChart = useMemo(() => {
    if (!selectedTeamForChart) {
      return { labels: [] as string[], starters: [] as number[] };
    }

    const perRace: Record<
      string,
      { date: string; starters: number }
    > = {};

    filteredRaceResults.forEach((result) => {
      if (result.team !== selectedTeamForChart) {
        return;
      }
      const meta = raceCodeToMeta[result.race_code];
      if (!meta) {
        return;
      }
      const key = `${meta.race_date}|${result.race_code}`;
      if (!perRace[key]) {
        perRace[key] = { date: meta.race_date, starters: 0 };
      }
      perRace[key].starters += 1;
    });

    const sorted = Object.values(perRace).sort((a, b) =>
      a.date.localeCompare(b.date)
    );
    return {
      labels: sorted.map((entry) => formatShortDate(entry.date)),
      starters: sorted.map((entry) => entry.starters),
    };
  }, [filteredRaceResults, raceCodeToMeta, selectedTeamForChart]);

  const startsTimelineByRace = useMemo(() => {
    const perDate: Record<string, number> = {};

    filteredRaceResults.forEach((result) => {
      const meta = raceCodeToMeta[result.race_code];
      if (!meta) {
        return;
      }
      const key = meta.race_date;
      perDate[key] = (perDate[key] || 0) + 1;
    });

    const entries = Object.entries(perDate).sort(([a], [b]) =>
      a.localeCompare(b)
    );
    return {
      labels: entries.map(([date]) => formatShortDate(date)),
      values: entries.map(([, value]) => value),
    };
  }, [filteredRaceResults, raceCodeToMeta]);

  const startsTimelineByMonth = useMemo(() => {
    const perMonth: Record<string, number> = {};

    filteredRaceResults.forEach((result) => {
      const meta = raceCodeToMeta[result.race_code];
      if (!meta) {
        return;
      }
      const date = new Date(meta.race_date);
      if (Number.isNaN(date.getTime())) {
        return;
      }
      const key = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, '0')}`;
      perMonth[key] = (perMonth[key] || 0) + 1;
    });

    const entries = Object.entries(perMonth).sort(([a], [b]) =>
      a.localeCompare(b)
    );
    return {
      labels: entries.map(([monthKey]) => formatMonthLabel(monthKey)),
      values: entries.map(([, value]) => value),
    };
  }, [filteredRaceResults, raceCodeToMeta]);

  const topTeamsForTimeline = useMemo(() => {
    const threshold = 10;
    return teamSeasonStats
      .filter((team) => team.totalStarts >= threshold)
      .slice(0, 5);
  }, [teamSeasonStats]);

  if (isLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Center h="50vh">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            size="xl"
          />
        </Center>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Box mb={8} position="relative">
        <Image
          src="/img/statistics.jpg"
          alt="Statistics"
          width="100%"
          height="300px"
          objectFit="cover"
          borderRadius="lg"
          fallback={<Box bg="gray.200" h="300px" borderRadius="lg" />}
        />
        <Box
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          p={6}
          background="linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0))"
          borderBottomRadius="lg"
        >
          <Heading color="white">Statistics &amp; Fun Facts</Heading>
          <Text color="white" mt={2} fontSize="lg">
            Season nerd stats, club battles and jumper-of-the-year awards.
          </Text>
        </Box>
      </Box>

      {fetchError && (
        <Box mb={6}>
          <Alert status="warning" variant="subtle" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle fontSize="sm">Statistics are partially loaded</AlertTitle>
              <AlertDescription fontSize="sm">{fetchError}</AlertDescription>
            </Box>
          </Alert>
        </Box>
      )}

      {/* Global filters for season and league, applied across all statistic tabs. */}
      {seasons.length > 0 && (
        <Box mb={6}>
          <Wrap spacing={4} align="center">
            <WrapItem>
              <Box>
                <Text mb={1} fontWeight="medium">
                  Season scope
                </Text>
                <Select
                  size="sm"
                  value={selectedSeason}
                  onChange={(e) => setSelectedSeason(e.target.value)}
                  maxW="200px"
                >
                  <option value="all">All seasons</option>
                  {seasons.map((season) => (
                    <option key={season} value={season}>
                      {season}
                    </option>
                  ))}
                </Select>
              </Box>
            </WrapItem>
            {leagues.length > 0 && (
              <WrapItem>
                <Box>
                  <Text mb={1} fontWeight="medium">
                    League
                  </Text>
                  <Select
                    size="sm"
                    value={selectedLeague}
                    onChange={(e) => setSelectedLeague(e.target.value)}
                    maxW="220px"
                  >
                    <option value="all">All leagues</option>
                    {leagues.map((league) => (
                      <option key={league} value={league}>
                        {league}
                      </option>
                    ))}
                  </Select>
                </Box>
              </WrapItem>
            )}
          </Wrap>
        </Box>
      )}

      <Tabs
        colorScheme="blue"
        variant="enclosed"
        isFitted
        overflowX="auto"
        size="sm"
      >
        <TabList>
          <Tab _selected={tabSelectedStyles}>Overview</Tab>
          <Tab _selected={tabSelectedStyles}>Racers</Tab>
          <Tab _selected={tabSelectedStyles}>Teams &amp; Clubs</Tab>
          <Tab _selected={tabSelectedStyles}>Timeline</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            <StatGroup mb={6}>
              <Stat>
                <StatLabel>Total races</StatLabel>
                <StatNumber>{totalRaces}</StatNumber>
                <StatHelpText>
                  In selected scope
                  {selectedSeason !== 'all' ? ` (${selectedSeason})` : ''}
                </StatHelpText>
              </Stat>
              <Stat>
                <StatLabel>Total race starts</StatLabel>
                <StatNumber>{totalStarts}</StatNumber>
                <StatHelpText>
                  All categories, all teams
                </StatHelpText>
              </Stat>
              <Stat>
                <StatLabel>Unique racers</StatLabel>
                <StatNumber>{totalRacers}</StatNumber>
                <StatHelpText>Started at least once</StatHelpText>
              </Stat>
              <Stat>
                <StatLabel>Active clubs</StatLabel>
                <StatNumber>{totalClubs}</StatNumber>
                <StatHelpText>With at least one starter</StatHelpText>
              </Stat>
            </StatGroup>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              <Box
                p={4}
                borderWidth="1px"
                borderRadius="lg"
                bg={cardBg}
                boxShadow="sm"
                minH="260px"
              >
                <Heading size="md" mb={3}>
                  Top teams by points
                </Heading>
                <Text fontSize="sm" mb={4}>
                  Points 1–6: 100, 80, 60, 50, 45, 40 per place.
                </Text>
                {topTeamsForOverview.length === 0 ? (
                  <Center h="160px">
                    <Text fontSize="sm">No team data in selected scope.</Text>
                  </Center>
                ) : (
                  <Box height="220px">
                    <Bar
                      data={{
                        labels: topTeamsForOverview.map((team) => team.team),
                        datasets: [
                          {
                            label: 'Points',
                            data: topTeamsForOverview.map((team) => team.points),
                            backgroundColor: chartColors.blues,
                            borderColor: 'white',
                            borderWidth: 1,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                          title: {
                            display: false,
                          },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                          },
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>

              <Box
                p={4}
                borderWidth="1px"
                borderRadius="lg"
                bg={cardBg}
                boxShadow="sm"
                minH="260px"
              >
                <Heading size="md" mb={3}>
                  Race disciplines
                </Heading>
                <Text fontSize="sm" mb={4}>
                  Distribution of races by discipline within the current filter.
                </Text>
                {disciplineDistribution.labels.length === 0 ? (
                  <Center h="160px">
                    <Text fontSize="sm">No discipline data for this scope.</Text>
                  </Center>
                ) : (
                  <Box height="220px">
                    <Doughnut
                      data={{
                        labels: disciplineDistribution.labels,
                        datasets: [
                          {
                            data: disciplineDistribution.data,
                            backgroundColor: chartColors.blues,
                            borderColor: 'white',
                            borderWidth: 1,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { position: 'right' as const },
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>

              <Box
                p={4}
                borderWidth="1px"
                borderRadius="lg"
                bg={cardBg}
                boxShadow="sm"
                minH="260px"
              >
                <Heading size="md" mb={3}>
                  Racers by country
                </Heading>
                <Text fontSize="sm" mb={4}>
                  Number of unique racers per country (from database).
                </Text>
                {racersByCountry.labels.length === 0 ? (
                  <Center h="160px">
                    <Text fontSize="sm">No racer country data for this scope.</Text>
                  </Center>
                ) : (
                  <Box height="220px">
                    <Bar
                      data={{
                        labels: racersByCountry.labels,
                        datasets: [
                          {
                            label: 'Racers',
                            data: racersByCountry.data,
                            backgroundColor: chartColors.blues,
                            borderColor: 'white',
                            borderWidth: 1,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                          title: { display: false },
                        },
                        scales: {
                          x: {
                            ticks: {
                              maxRotation: 0,
                              font: { size: 11 },
                            },
                          },
                          y: {
                            beginAtZero: true,
                            ticks: { stepSize: 1 },
                          },
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            </SimpleGrid>
          </TabPanel>

          <TabPanel px={0}>
            <Heading size="md" mb={4}>
              Biggest competitors
            </Heading>
            <Text fontSize="sm" mb={4}>
              Racers with the highest number of starts in the selected scope. Ties
              are broken by total career starts and best position.
            </Text>
            {biggestCompetitors.length === 0 ? (
              <Text fontSize="sm">No racer data for this scope.</Text>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} mb={8}>
                {biggestCompetitors.map((racer, index) => (
                  <Link
                    key={racer.name}
                    as={RouterLink}
                    to={`/racer/${encodeURIComponent(racer.name)}`}
                    _hover={{ textDecoration: 'none' }}
                    display="block"
                  >
                    <Box
                      p={4}
                      borderWidth="1px"
                      borderRadius="lg"
                      bg={cardBg}
                      boxShadow="sm"
                      cursor="pointer"
                      _hover={{
                        transform: 'translateY(-3px)',
                        boxShadow: 'md',
                        borderColor: 'blue.300',
                      }}
                      transition="all 0.2s"
                    >
                    <Flex align="center" mb={3}>
                      <Avatar
                        name={racer.name}
                        size="sm"
                        mr={3}
                        bg={brandColors.light}
                        color="black"
                      />
                      <Box flex="1">
                        <Text fontWeight="bold" isTruncated>
                          {racer.name}
                        </Text>
                        <Text fontSize="xs" color="gray.500" isTruncated>
                          {racer.team || 'No club'}
                        </Text>
                      </Box>
                      <Badge
                        colorScheme={
                          index === 0 ? 'yellow' : index === 1 ? 'gray' : 'orange'
                        }
                      >
                        {index === 0
                          ? 'Gold grinder'
                          : index === 1
                          ? 'Silver grinder'
                          : index === 2
                          ? 'Bronze grinder'
                          : 'Finalist'}
                      </Badge>
                    </Flex>
                    <Flex justify="space-between" fontSize="sm" mb={1}>
                      <Text>Starts this scope:</Text>
                      <Text fontWeight="semibold">{racer.seasonStarts}</Text>
                    </Flex>
                    <Flex justify="space-between" fontSize="sm" mb={1}>
                      <Text>Total career starts:</Text>
                      <Text>{racer.totalStarts}</Text>
                    </Flex>
                    <Flex justify="space-between" fontSize="sm">
                      <Text>Best position:</Text>
                      <Text>
                        {racer.bestPosition
                          ? formatPosition(racer.bestPosition)
                          : 'N/A'}
                      </Text>
                    </Flex>
                    {racer.lastRace && (
                      <Text mt={2} fontSize="xs" color="gray.500">
                        Last race: {formatShortDate(racer.lastRace)}
                      </Text>
                    )}
                    </Box>
                  </Link>
                ))}
              </SimpleGrid>
            )}

            <Heading size="md" mb={4}>
              Jumpers of the year
            </Heading>
            <Text fontSize="sm" mb={4}>
              Racers whose average finishing position improved the most between the
              first and second half of their races in the selected scope.
            </Text>
            {jumpersOfYear.length === 0 ? (
              <Text fontSize="sm">Not enough races to evaluate improvements.</Text>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} mb={8}>
                {jumpersOfYear.map((racer) => (
                  <Link
                    key={racer.name}
                    as={RouterLink}
                    to={`/racer/${encodeURIComponent(racer.name)}`}
                    _hover={{ textDecoration: 'none' }}
                    display="block"
                  >
                    <Box
                      p={4}
                      borderWidth="1px"
                      borderRadius="lg"
                      bg={cardBg}
                      boxShadow="sm"
                      cursor="pointer"
                      _hover={{
                        transform: 'translateY(-3px)',
                        boxShadow: 'md',
                        borderColor: 'green.300',
                      }}
                      transition="all 0.2s"
                    >
                    <Flex align="center" mb={3}>
                      <Avatar
                        name={racer.name}
                        size="sm"
                        mr={3}
                        bg="green.100"
                        color="green.800"
                      />
                      <Box flex="1">
                        <Text fontWeight="bold" isTruncated>
                          {racer.name}
                        </Text>
                        <Text fontSize="xs" color="gray.500" isTruncated>
                          {racer.team || 'No club'}
                        </Text>
                      </Box>
                      <Tooltip
                        label={`Average position improved by ${racer.improvement.toFixed(
                          1
                        )} places.`}
                      >
                        <Tag colorScheme="green">
                          <TriangleUpIcon mr={1} /> Rocket booster
                        </Tag>
                      </Tooltip>
                    </Flex>
                    <Flex justify="space-between" fontSize="sm" mb={1}>
                      <Text>Races in scope:</Text>
                      <Text fontWeight="semibold">{racer.seasonStarts}</Text>
                    </Flex>
                    <Flex justify="space-between" fontSize="sm" mb={1}>
                      <Text>First half avg:</Text>
                      <Text>{racer.firstHalfAvg.toFixed(1)}</Text>
                    </Flex>
                    <Flex justify="space-between" fontSize="sm">
                      <Text>Second half avg:</Text>
                      <Text>{racer.secondHalfAvg.toFixed(1)}</Text>
                    </Flex>
                    </Box>
                  </Link>
                ))}
              </SimpleGrid>
            )}

            <Heading size="md" mb={4}>
              Consistency beasts
            </Heading>
            <Text fontSize="sm" mb={4}>
              Racers with stable, strong finishes across many races in the selected
              scope.
            </Text>
            {consistencyBeasts.length === 0 ? (
              <Text fontSize="sm">No consistent racers detected for this scope.</Text>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {consistencyBeasts.map((racer) => (
                  <Link
                    key={racer.name}
                    as={RouterLink}
                    to={`/racer/${encodeURIComponent(racer.name)}`}
                    _hover={{ textDecoration: 'none' }}
                    display="block"
                  >
                    <Box
                      p={4}
                      borderWidth="1px"
                      borderRadius="lg"
                      bg={cardBg}
                      boxShadow="sm"
                      cursor="pointer"
                      _hover={{
                        transform: 'translateY(-3px)',
                        boxShadow: 'md',
                        borderColor: 'purple.300',
                      }}
                      transition="all 0.2s"
                    >
                    <Flex align="center" mb={3}>
                      <Avatar
                        name={racer.name}
                        size="sm"
                        mr={3}
                        bg="purple.100"
                        color="purple.800"
                      />
                      <Box flex="1">
                        <Text fontWeight="bold" isTruncated>
                          {racer.name}
                        </Text>
                        <Text fontSize="xs" color="gray.500" isTruncated>
                          {racer.team || 'No club'}
                        </Text>
                      </Box>
                      <Tooltip label="Small spread of positions, good average." fontSize="xs">
                        <Tag colorScheme="purple">
                          <StarIcon mr={1} /> Metronome
                        </Tag>
                      </Tooltip>
                    </Flex>
                    <Flex justify="space-between" fontSize="sm" mb={1}>
                      <Text>Races in scope:</Text>
                      <Text fontWeight="semibold">{racer.seasonStarts}</Text>
                    </Flex>
                    <Flex justify="space-between" fontSize="sm" mb={1}>
                      <Text>Average position:</Text>
                      <Text>{racer.averagePosition.toFixed(1)}</Text>
                    </Flex>
                    <Flex justify="space-between" fontSize="sm">
                      <Text>Std. deviation:</Text>
                      <Text>{racer.stddevPosition.toFixed(2)}</Text>
                    </Flex>
                    </Box>
                  </Link>
                ))}
              </SimpleGrid>
            )}
          </TabPanel>

          <TabPanel px={0}>
            <Heading size="md" mb={4}>
              Team / club of the year
            </Heading>
            <Text fontSize="sm" mb={4}>
              Ranking by points, race starts and win-rate style efficiency (points
              per unique team member).
            </Text>

            {teamSeasonStats.length === 0 ? (
              <Text fontSize="sm">No team statistics for this scope.</Text>
            ) : (
              <>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
                  <Box
                    p={4}
                    borderWidth="1px"
                    borderRadius="lg"
                    bg={cardBg}
                    boxShadow="sm"
                  >
                    <Text fontSize="xs" textTransform="uppercase" mb={1}>
                      Competition monsters
                    </Text>
                    <Heading size="sm" mb={2}>
                      Most race starts
                    </Heading>
                    {teamSeasonStats
                      .slice()
                      .sort((a, b) => b.totalStarts - a.totalStarts)
                      .slice(0, 1)
                      .map((team) => (
                        <Link
                          key={team.team}
                          as={RouterLink}
                          to={`/teamDetail/${encodeURIComponent(team.team)}`}
                          _hover={{ textDecoration: 'none' }}
                          display="block"
                        >
                          <Box
                            _hover={{ color: 'blue.600' }}
                            cursor="pointer"
                          >
                            <Text fontWeight="bold">{team.team}</Text>
                            <Text fontSize="sm">
                              {team.totalStarts} starts, {team.memberCount} members
                            </Text>
                          </Box>
                        </Link>
                      ))}
                  </Box>

                  <Box
                    p={4}
                    borderWidth="1px"
                    borderRadius="lg"
                    bg={cardBg}
                    boxShadow="sm"
                  >
                    <Text fontSize="xs" textTransform="uppercase" mb={1}>
                      Points factory
                    </Text>
                    <Heading size="sm" mb={2}>
                      Cumulative points
                    </Heading>
                    {teamSeasonStats
                      .slice()
                      .sort((a, b) => b.points - a.points)
                      .slice(0, 1)
                      .map((team) => (
                        <Link
                          key={team.team}
                          as={RouterLink}
                          to={`/teamDetail/${encodeURIComponent(team.team)}`}
                          _hover={{ textDecoration: 'none' }}
                          display="block"
                        >
                          <Box
                            _hover={{ color: 'blue.600' }}
                            cursor="pointer"
                          >
                            <Text fontWeight="bold">{team.team}</Text>
                            <Text fontSize="sm">
                              {team.points} pts, {team.podiumFinishes} podiums
                            </Text>
                          </Box>
                        </Link>
                      ))}
                  </Box>

                  <Box
                    p={4}
                    borderWidth="1px"
                    borderRadius="lg"
                    bg={cardBg}
                    boxShadow="sm"
                  >
                    <Text fontSize="xs" textTransform="uppercase" mb={1}>
                      Sniper club
                    </Text>
                    <Heading size="sm" mb={2}>
                      Best win-rate
                    </Heading>
                    {teamSeasonStats
                      .filter((team) => team.totalStarts >= 8)
                      .slice()
                      .sort((a, b) => b.winRate - a.winRate)
                      .slice(0, 1)
                      .map((team) => (
                        <Link
                          key={team.team}
                          as={RouterLink}
                          to={`/teamDetail/${encodeURIComponent(team.team)}`}
                          _hover={{ textDecoration: 'none' }}
                          display="block"
                        >
                          <Box
                            _hover={{ color: 'blue.600' }}
                            cursor="pointer"
                          >
                            <Text fontWeight="bold">{team.team}</Text>
                            <Text fontSize="sm">
                              {team.winRate.toFixed(1)} pts per member
                            </Text>
                          </Box>
                        </Link>
                      ))}
                  </Box>
                </SimpleGrid>

                <Box
                  p={4}
                  borderWidth="1px"
                  borderRadius="lg"
                  bg={cardBg}
                  boxShadow="sm"
                  mb={6}
                >
                  <Heading size="sm" mb={4}>
                    Team leaderboard
                  </Heading>
                  <Box overflowX="auto">
                    <Box as="table" width="100%" fontSize="sm">
                      <Box as="thead">
                        <Box as="tr" textAlign="left">
                          <Box
                            as="th"
                            py={2}
                            px={1}
                            cursor="pointer"
                            userSelect="none"
                            onClick={() => handleTeamLeaderboardSort('team')}
                            _hover={{ bg: 'gray.100' }}
                          >
                            Team
                            {teamLeaderboardSort?.key === 'team' && (
                              teamLeaderboardSort.direction === 'asc'
                                ? <TriangleUpIcon ml={1} verticalAlign="middle" />
                                : <TriangleDownIcon ml={1} verticalAlign="middle" />
                            )}
                          </Box>
                          <Box
                            as="th"
                            py={2}
                            px={1}
                            cursor="pointer"
                            userSelect="none"
                            onClick={() => handleTeamLeaderboardSort('totalStarts')}
                            _hover={{ bg: 'gray.100' }}
                          >
                            Starts
                            {teamLeaderboardSort?.key === 'totalStarts' && (
                              teamLeaderboardSort.direction === 'asc'
                                ? <TriangleUpIcon ml={1} verticalAlign="middle" />
                                : <TriangleDownIcon ml={1} verticalAlign="middle" />
                            )}
                          </Box>
                          <Box
                            as="th"
                            py={2}
                            px={1}
                            cursor="pointer"
                            userSelect="none"
                            onClick={() => handleTeamLeaderboardSort('memberCount')}
                            _hover={{ bg: 'gray.100' }}
                          >
                            Members
                            {teamLeaderboardSort?.key === 'memberCount' && (
                              teamLeaderboardSort.direction === 'asc'
                                ? <TriangleUpIcon ml={1} verticalAlign="middle" />
                                : <TriangleDownIcon ml={1} verticalAlign="middle" />
                            )}
                          </Box>
                          <Box
                            as="th"
                            py={2}
                            px={1}
                            cursor="pointer"
                            userSelect="none"
                            onClick={() => handleTeamLeaderboardSort('podiumFinishes')}
                            _hover={{ bg: 'gray.100' }}
                          >
                            Podiums
                            {teamLeaderboardSort?.key === 'podiumFinishes' && (
                              teamLeaderboardSort.direction === 'asc'
                                ? <TriangleUpIcon ml={1} verticalAlign="middle" />
                                : <TriangleDownIcon ml={1} verticalAlign="middle" />
                            )}
                          </Box>
                          <Box
                            as="th"
                            py={2}
                            px={1}
                            cursor="pointer"
                            userSelect="none"
                            onClick={() => handleTeamLeaderboardSort('points')}
                            _hover={{ bg: 'gray.100' }}
                          >
                            Points
                            {teamLeaderboardSort?.key === 'points' && (
                              teamLeaderboardSort.direction === 'asc'
                                ? <TriangleUpIcon ml={1} verticalAlign="middle" />
                                : <TriangleDownIcon ml={1} verticalAlign="middle" />
                            )}
                          </Box>
                          <Box
                            as="th"
                            py={2}
                            px={1}
                            cursor="pointer"
                            userSelect="none"
                            onClick={() => handleTeamLeaderboardSort('winRate')}
                            _hover={{ bg: 'gray.100' }}
                          >
                            Win rate
                            {teamLeaderboardSort?.key === 'winRate' && (
                              teamLeaderboardSort.direction === 'asc'
                                ? <TriangleUpIcon ml={1} verticalAlign="middle" />
                                : <TriangleDownIcon ml={1} verticalAlign="middle" />
                            )}
                          </Box>
                        </Box>
                      </Box>
                      <Box as="tbody">
                        {sortedTeamLeaderboard.map((team) => (
                          <Box
                            as="tr"
                            key={team.team}
                            _hover={{ bg: 'gray.50' }}
                            cursor="pointer"
                            onClick={(e: React.MouseEvent) => {
                              if ((e.target as HTMLElement).closest('a')) return;
                              navigate(`/teamDetail/${encodeURIComponent(team.team)}`);
                            }}
                          >
                            <Box as="td" py={1} px={1} fontWeight="medium">
                              <Link
                                as={RouterLink}
                                to={`/teamDetail/${encodeURIComponent(team.team)}`}
                                _hover={{ textDecoration: 'none' }}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                              >
                                {team.team}
                              </Link>
                            </Box>
                            <Box as="td" py={1} px={1}>
                              {team.totalStarts}
                            </Box>
                            <Box as="td" py={1} px={1}>
                              {team.memberCount}
                            </Box>
                            <Box as="td" py={1} px={1}>
                              {team.podiumFinishes}
                            </Box>
                            <Box as="td" py={1} px={1}>
                              {team.points}
                            </Box>
                            <Box as="td" py={1} px={1}>
                              {team.winRate.toFixed(1)}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Box
                  p={4}
                  borderWidth="1px"
                  borderRadius="lg"
                  bg={cardBg}
                  boxShadow="sm"
                >
                  <Flex justify="space-between" align="center" mb={4}>
                    <Heading size="sm">Team history – starters per race</Heading>
                    <Select
                      size="sm"
                      maxW="240px"
                      value={selectedTeamForChart ?? ''}
                      onChange={(e) =>
                        setSelectedTeamForChart(
                          e.target.value || teamSeasonStats[0]?.team || null
                        )
                      }
                    >
                      {teamSeasonStats.map((team) => (
                        <option key={team.team} value={team.team}>
                          {team.team}
                        </option>
                      ))}
                    </Select>
                  </Flex>
                  {teamHistoryForChart.labels.length === 0 ? (
                    <Text fontSize="sm">
                      No race history for the selected team in this scope.
                    </Text>
                  ) : (
                    <Box height="260px">
                      <Bar
                        data={{
                          labels: teamHistoryForChart.labels,
                          datasets: [
                            {
                              label: 'Starters per race',
                              data: teamHistoryForChart.starters,
                              backgroundColor: brandColors.primary,
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: { beginAtZero: true },
                          },
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </>
            )}
          </TabPanel>

          <TabPanel px={0}>
            <Heading size="md" mb={4}>
              Starts over time
            </Heading>
            <Text fontSize="sm" mb={4}>
              How many racer starts we see per race event and per calendar month
              within the current filter.
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box
                p={4}
                borderWidth="1px"
                borderRadius="lg"
                bg={cardBg}
                boxShadow="sm"
              >
                <Heading size="sm" mb={3}>
                  Starts per race day
                </Heading>
                {startsTimelineByRace.labels.length === 0 ? (
                  <Text fontSize="sm">No race data for this scope.</Text>
                ) : (
                  <Box height="220px">
                    <Line
                      data={{
                        labels: startsTimelineByRace.labels,
                        datasets: [
                          {
                            label: 'Starts',
                            data: startsTimelineByRace.values,
                            borderColor: brandColors.primary,
                            backgroundColor: brandColors.light,
                            tension: 0.2,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                        },
                        scales: {
                          y: { beginAtZero: true },
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>

              <Box
                p={4}
                borderWidth="1px"
                borderRadius="lg"
                bg={cardBg}
                boxShadow="sm"
              >
                <Heading size="sm" mb={3}>
                  Starts per month
                </Heading>
                {startsTimelineByMonth.labels.length === 0 ? (
                  <Text fontSize="sm">No monthly data for this scope.</Text>
                ) : (
                  <Box height="220px">
                    <Bar
                      data={{
                        labels: startsTimelineByMonth.labels,
                        datasets: [
                          {
                            label: 'Starts',
                            data: startsTimelineByMonth.values,
                            backgroundColor: chartColors.blues,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                        },
                        scales: {
                          y: { beginAtZero: true },
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            </SimpleGrid>

            <Box mt={8}>
              <Heading size="md" mb={4}>
                Top clubs – participation scope
              </Heading>
              <Text fontSize="sm" mb={4}>
                Clubs with the highest number of starts in the selected scope. Use
                this to see who keeps ski lifts busiest.
              </Text>
              {topTeamsForTimeline.length === 0 ? (
                <Text fontSize="sm">No club participation data for this scope.</Text>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  {topTeamsForTimeline.map((team) => (
                    <Link
                      key={team.team}
                      as={RouterLink}
                      to={`/teamDetail/${encodeURIComponent(team.team)}`}
                      _hover={{ textDecoration: 'none' }}
                      display="block"
                    >
                      <Box
                        p={4}
                        borderWidth="1px"
                        borderRadius="lg"
                        bg={cardBg}
                        boxShadow="sm"
                        cursor="pointer"
                        _hover={{
                          transform: 'translateY(-3px)',
                          boxShadow: 'md',
                          borderColor: 'blue.300',
                        }}
                        transition="all 0.2s"
                      >
                      <Flex align="center" mb={2}>
                        <Tag colorScheme="blue" mr={2}>
                          Club
                        </Tag>
                        <Text fontWeight="bold" isTruncated>
                          {team.team}
                        </Text>
                      </Flex>
                      <Flex justify="space-between" fontSize="sm" mb={1}>
                        <Text>Starts:</Text>
                        <Text fontWeight="semibold">{team.totalStarts}</Text>
                      </Flex>
                      <Flex justify="space-between" fontSize="sm" mb={1}>
                        <Text>Members in scope:</Text>
                        <Text>{team.memberCount}</Text>
                      </Flex>
                      <Flex justify="space-between" fontSize="sm">
                        <Text>Points:</Text>
                        <Text>{team.points}</Text>
                      </Flex>
                      </Box>
                    </Link>
                  ))}
                </SimpleGrid>
              )}
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Container>
  );
}